import React from "react";
import { useBLE } from "../../../hooks/useBle";
// Reusable Circular Progress Component
const CircularProgress = ({
  progress,
  size = 32,
}: {
  progress: number;
  size?: number;
}) => {
  const radius = (size - 4) / 2;
  const circumference = radius * 2 * Math.PI;
  const strokeDasharray = circumference;
  const strokeDashoffset = circumference - (progress / 100) * circumference;

  return (
    <div className="relative inline-flex items-center justify-center">
      <svg width={size} height={size} className="transform -rotate-90">
        {/* Background circle */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          stroke="currentColor"
          strokeWidth="2"
          fill="transparent"
          className="text-gray-600"
        />
        {/* Progress circle */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          stroke="currentColor"
          strokeWidth="2"
          fill="transparent"
          strokeDasharray={strokeDasharray}
          strokeDashoffset={strokeDashoffset}
          className="text-cyan-400 transition-all duration-300 ease-out"
          strokeLinecap="round"
        />
      </svg>
      {/* Percentage text */}
      <div className="absolute inset-0 flex items-center justify-center">
        <span className="text-xs font-medium text-cyan-400">
          {Math.round(progress)}%
        </span>
      </div>
    </div>
  );
};

// Enhanced Adherence Loading Component
export const AdherenceLoading = () => {
  const { adherenceLoading, adherenceProgress } = useBLE();
  console.log("Adherence Loading:", adherenceLoading, adherenceProgress);
  if (adherenceLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-16">
        {adherenceProgress > 0 ? (
          <CircularProgress progress={adherenceProgress} />
        ) : (
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cyan-400"></div>
        )}
        <span className="text-gray-400 mt-4">
          {adherenceProgress > 0
            ? `Loading adherence data... ${Math.round(adherenceProgress)}%`
            : "Getting information"}
        </span>
      </div>
    );
  }
  return null;
};

// Enhanced Curves Loading Component
export const CurvesLoading = () => {
  const { curveLoading, curvesProgress } = useBLE();
  if (curveLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-16">
        {curvesProgress > 0 ? (
          <CircularProgress progress={curvesProgress} />
        ) : (
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cyan-400"></div>
        )}
        <span className="text-gray-400 mt-4">
          {curvesProgress > 0
            ? `Loading curves data... ${Math.round(curvesProgress)}%`
            : "Getting information"}
        </span>
      </div>
    );
  }
  return null;
};

// Enhanced Titration Loading Component
export const TitrationLoading = () => {
  const { titrationLoading, titrationProgress } = useBLE();
  if (titrationLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-16">
        {titrationProgress > 0 ? (
          <CircularProgress progress={titrationProgress} />
        ) : (
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cyan-400"></div>
        )}
        <span className="text-gray-400 mt-4">
          {titrationProgress > 0
            ? `Loading titration data... ${Math.round(titrationProgress)}%`
            : "Getting information"}
        </span>
      </div>
    );
  }
  return null;
};

// Usage in your components:
// Replace your existing loading blocks with:

// For Adherence:
// <AdherenceLoading adherenceProgress={adherenceProgress} />

// For Curves:
// <CurvesLoading curvesProgress={curvesProgress} />

// For Titration:
// <TitrationLoading titrationProgress={titrationProgress} />
